import { DocumentAttachmentChangesContext } from '@/context/DocumentAttachmentChangesContext';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentAttachService } from '@services/DocumentAttachService';
import type { IDataTreeService } from '@services/IDataTreeService';
import { IDataGridColumnDescription } from '@models/response/IGridDescriptionResponse';
import { ITreeListDescriptionResponse } from '@models/response/ITreeListDescriptionResponse';
import { IAttachColumn, IAttachFilter, IAttachGridOptions, IAttachRow, IAttachSort } from './AttachGrid.types';
import { buildDataParams, buildLoadOptions, initialSort, mapColumn, toAttachRow } from './attachLoadOptions';
import { sortRows } from './attachSort';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { DocumentDataRefreshContext } from '@/context/DocumentDataRefreshContext';

const flip = (dir: 'asc' | 'desc'): 'asc' | 'desc' => (dir === 'asc' ? 'desc' : 'asc');

function mapGridOptions(scheme: ITreeListDescriptionResponse): IAttachGridOptions {
    return {
        pageSize: scheme.paging?.pageSize ?? 10,
        // бэк часто отдаёт пустой массив — `??` его не перехватит, нужен явный фоллбэк
        pagerSizes: scheme.pager?.pagerSizes?.length ? scheme.pager.pagerSizes : [5, 10, 20],
        showPageSizeSelector: scheme.pager?.showPageSizeSelector ?? true,
        showPageInfo: scheme.pager?.showInfo ?? true,
        filterRowVisible: scheme.filterRow?.visible ?? false,
        selectionMode: scheme.selection?.mode ?? 'multiple',
        selectionRecursive: scheme.selection?.recursive ?? false,
        allowSelectAll: scheme.selection?.allowSelectAll ?? true,
        allowColumnChooser: scheme.options?.allowColumnChooser ?? true,
        exportUrl: scheme.options?.export_url ?? '',
        excelFormat: scheme.options?.excel_format ?? false,
        noDataText: scheme.options?.noDataText ?? 'Нет данных',
        addAttachActionKey: readAddOption(scheme, 'addAttachActionKey'),
        stateKey: scheme.options?.stateKey || undefined,
    };
}

/** Читает значение из scheme.options.addOptions (бэк отдаёт массив key/value). */
function readAddOption(scheme: ITreeListDescriptionResponse, key: string): string | undefined {
    const list = (scheme.options as unknown as { addOptions?: Array<{ key: string; value: unknown }> })?.addOptions;
    if (!Array.isArray(list)) return undefined;
    const found = list.find((o) => o.key === key)?.value;
    return found == null || found === '' ? undefined : String(found);
}

export interface IUseAttachGridData {
    ready: boolean;
    loadedChangeToken?: string;
    loadedSpotlightKeys: string[] | null;
    spotlightKeys: string[] | null;
    setSpotlight: (keys: string[] | null) => void;
    columns: IAttachColumn[];
    /** Исходные колонки схемы — для жучка, без нормализации грида. */
    schemeColumns: IDataGridColumnDescription[];
    options: IAttachGridOptions | null;
    /** Строки текущей страницы (отсортированы и нарезаны на клиенте). */
    rows: IAttachRow[];
    /** Весь отфильтрованный набор (для выбора и групповых действий между страницами). */
    allRows: IAttachRow[];
    totalCount: number;
    loading: boolean;
    error: boolean;
    page: number;
    pageSize: number;
    search: string;
    sort: IAttachSort[];
    filters: Record<string, IAttachFilter>;
    expandedKeys: Set<string>;
    childrenByParent: Record<string, IAttachRow[]>;
    loadingChildren: Set<string>;
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
    setSearch: (value: string) => void;
    toggleSort: (field: string, additive: boolean) => void;
    setSingleSort: (field: string, dir: 'asc' | 'desc') => void;
    setFilter: (field: string, op: string, value: unknown) => void;
    clearFilters: () => void;
    setColumnsVisibility: (hidden: string[]) => void;
    setSort: (sort: IAttachSort[]) => void;
    toggleExpand: (key: string) => void;
    refresh: () => void;
    buildExportLoadOptions: () => Record<string, unknown>;
}

/** Слой данных грида вложений: схема → колонки/опции, серверные данные с
 *  пейджингом/сортировкой/фильтрами, ленивая загрузка версий. Бэкенд не меняется —
 *  запрос строится тем же конвертером (getLoadOptionsQuery), что и текущий DevExtreme-грид. */
export function useAttachGridData(docId: string, tableKey: string): IUseAttachGridData {
    const runtime = useContext(DocumentRuntimeContext);
    const documentDataRefresh = useContext(DocumentDataRefreshContext);
    const attachmentChanges = useContext(DocumentAttachmentChangesContext);
    const attachmentRevision = attachmentChanges?.documentId === docId ? attachmentChanges.revision : undefined;
    const [loadedChangeToken, setLoadedChangeToken] = useState<string>();
    const [loadedSpotlightKeys, setLoadedSpotlightKeys] = useState<string[] | null>(null);
    const [spotlightKeys, setSpotlightKeys] = useState<string[] | null>(null);
    // Overwrite replaces the part key; keep the spotlight attached to its logical file.
    const spotlightLogicalIds = useRef<string[] | null>(null);
    const restorePage = useRef(0);
    const attachmentItems = attachmentChanges?.documentId === docId ? attachmentChanges.items : undefined;
    useEffect(() => {
        if (!spotlightLogicalIds.current || !attachmentItems) return;
        const logicalIds = new Set(spotlightLogicalIds.current);
        const keys = attachmentItems.filter((item) => logicalIds.has(item.logicalFileId)).map((item) => item.attachmentKey);
        setSpotlightKeys((previous) => previous && (previous.length !== keys.length || previous.some((key, index) => key !== keys[index]))
            ? keys : previous);
    }, [attachmentItems]);
    const refreshRevision =
        runtime.mode === 'live' && documentDataRefresh?.docId === docId ? documentDataRefresh.revision : undefined;
    const service = useMemo<IDataTreeService | undefined>(
        () =>
            runtime.mode === 'supportPreview'
                ? runtime.dataSource?.attachmentsService
                : new DocumentAttachService(docId),
        [docId, runtime.dataSource?.attachmentsService, runtime.mode],
    );

    const [options, setOptions] = useState<IAttachGridOptions | null>(null);
    const [columns, setColumns] = useState<IAttachColumn[]>([]);
    const [schemeColumns, setSchemeColumns] = useState<IDataGridColumnDescription[]>([]);
    const [rows, setRows] = useState<IAttachRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const [page, setPageState] = useState(0);
    const [pageSize, setPageSizeState] = useState(10);
    const [search, setSearchState] = useState('');
    const [sort, setSort] = useState<IAttachSort[]>([]);
    const [filters, setFilters] = useState<Record<string, IAttachFilter>>({});

    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    const [childrenByParent, setChildrenByParent] = useState<Record<string, IAttachRow[]>>({});
    const [loadingChildren, setLoadingChildren] = useState<Set<string>>(new Set());

    const reqIdRef = useRef(0);
    const ready = columns.length > 0;
    const expandedRef = useRef(expandedKeys);
    expandedRef.current = expandedKeys;
    const setSpotlight = useCallback((keys: string[] | null) => {
        if (keys) {
            if (!spotlightKeys) restorePage.current = page;
            setPageState(0);
        } else setPageState(restorePage.current);
        spotlightLogicalIds.current = keys && attachmentItems
            ? attachmentItems.filter((item) => keys.includes(item.attachmentKey)).map((item) => item.logicalFileId)
            : null;
        setSpotlightKeys(keys);
    }, [page, spotlightKeys, attachmentItems]);

    // Колонка-«имя файла» для тулбар-поиска (серверный contains).
    const nameField = useMemo(() => {
        const textCols = columns.filter((c) => c.show && c.filterKind === 'text');
        const preferred = textCols.find((c) => /FileName|Title|Name/i.test(c.field)) ?? textCols[0] ?? columns[0];
        return preferred?.field;
    }, [columns]);

    // ----- Схема: колонки, опции, преднастроенная сортировка -----
    useEffect(() => {
        let alive = true;
        reqIdRef.current += 1;
        setSpotlightKeys(null);
        spotlightLogicalIds.current = null;
        setLoadedChangeToken(undefined);
        setLoadedSpotlightKeys(null);
        setColumns([]);
        setSchemeColumns([]);
        setOptions(null);
        setRows([]);
        setTotalCount(0);
        setExpandedKeys(new Set());
        setChildrenByParent({});
        if (!service) {
            setError(true);
            return;
        }
        setError(false);
        service
            .fetchScheme(tableKey)
            .then((res) => {
                if (!alive) return;
                const scheme = res.data;
                setColumns(scheme.columns.map(mapColumn).filter((c): c is IAttachColumn => c !== null));
                setSchemeColumns(scheme.columns ?? []);
                setOptions(mapGridOptions(scheme));
                setSort(initialSort(scheme.columns));
                setPageSizeState(scheme.paging?.pageSize ?? 10);
                setFilters({});
                setSearchState('');
                setPageState(0);
            })
            .catch(() => {
                if (alive) setError(true);
            });
        return () => {
            alive = false;
        };
    }, [service, tableKey]);

    const activeFilters = useCallback((): IAttachFilter[] => {
        if (spotlightKeys) return [];
        const list = [...Object.values(filters)];
        if (search && nameField) list.push({ field: nameField, op: 'contains', value: search });
        return list;
    }, [filters, search, nameField, spotlightKeys]);

    // ----- Корневые данные -----
    const loadRoot = useCallback(async () => {
        if (!ready || !service) return;
        const reqId = ++reqIdRef.current;
        setLoading(true);
        setError(false);
        try {
            // Бэкенд attachData игнорирует sort/skip/take и отдаёт весь набор — поэтому
            // запрашиваем всё (фильтр он уважает), а сортировку и пейджинг применяем на
            // клиенте. take с большим запасом — на случай бэка, который всё же постранично.
            const params = buildDataParams(columns, {
                skip: 0,
                take: 100000,
                requireTotalCount: true,
                sort: [],
                filters: activeFilters(),
            });
            const res = await service.fetchData(tableKey, null, params);
            if (reqId !== reqIdRef.current) return;
            const nextRows = (res.data.data ?? []).map(toAttachRow);
            setRows(nextRows);
            setLoadedChangeToken(attachmentRevision);
            setLoadedSpotlightKeys(spotlightKeys);
            setTotalCount(res.data.totalCount ?? 0);
            const retained = [...expandedRef.current].filter((key) => nextRows.some((row) => row.key === key));
            setExpandedKeys(new Set(retained));
            const children = await Promise.all(retained.map(async (key) => {
                try {
                    const response = await service.fetchData(tableKey, key, params);
                    return [key, (response.data.data ?? []).map(toAttachRow)] as const;
                } catch {
                    return [key, [] as IAttachRow[]] as const;
                }
            }));
            if (reqId !== reqIdRef.current) return;
            setChildrenByParent(Object.fromEntries(children));
        } catch {
            if (reqId !== reqIdRef.current) return;
            setError(true);
            setRows([]);
            setTotalCount(0);
        } finally {
            if (reqId === reqIdRef.current) setLoading(false);
        }
    }, [ready, columns, activeFilters, service, tableKey, attachmentRevision, spotlightKeys]);

    useEffect(() => {
        // Обновление карточки сохраняет грид смонтированным. Перечитываем только строки:
        // повторная загрузка схемы сбросила бы поиск, фильтры, сортировку и размер страницы.
        let timer: ReturnType<typeof setTimeout>;
        const reload = () => {
            if (document.querySelector('[role="dialog"][aria-modal="true"]')) timer = setTimeout(reload, 400);
            else void loadRoot();
        };
        reload();
        return () => { clearTimeout(timer); reqIdRef.current += 1; };
    }, [loadRoot, refreshRevision]);

    // ----- Версии (ленивая загрузка детей) -----
    const toggleExpand = useCallback(
        (key: string) => {
            const willExpand = !expandedKeys.has(key);
            setExpandedKeys((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
            });
            if (!willExpand || childrenByParent[key] || loadingChildren.has(key)) return;

            setLoadingChildren((prev) => new Set(prev).add(key));
            // Тот же reqId-guard, что и в loadRoot: refresh()/loadRoot инкрементят reqIdRef
            // и обнуляют childrenByParent. Ответ версий, пришедший после этого, не должен
            // записать устаревший список (всплыл бы при следующем раскрытии из кэша на :188).
            const gen = reqIdRef.current;
            const params = buildDataParams(columns, { sort });
            if (!service) return;
            service
                .fetchData(tableKey, key, params)
                .then((res) => {
                    if (gen !== reqIdRef.current) return;
                    setChildrenByParent((prev) => ({ ...prev, [key]: (res.data.data ?? []).map(toAttachRow) }));
                })
                .catch(() => {
                    if (gen !== reqIdRef.current) return;
                    setChildrenByParent((prev) => ({ ...prev, [key]: [] }));
                })
                .finally(() => {
                    setLoadingChildren((prev) => {
                        const next = new Set(prev);
                        next.delete(key);
                        return next;
                    });
                });
        },
        [expandedKeys, childrenByParent, loadingChildren, columns, sort, service, tableKey],
    );

    // ----- Сеттеры -----
    const setPage = useCallback((p: number) => setPageState(Math.max(0, p)), []);
    const setPageSize = useCallback((s: number) => {
        setPageSizeState(s);
        setPageState(0);
    }, []);

    const setSearch = useCallback((value: string) => {
        if (value === search) return;
        setSpotlightKeys(null);
        setSearchState(value);
        setPageState(0);
    }, [search]);

    const toggleSort = useCallback((field: string, additive: boolean) => {
        setSort((prev) => {
            const existing = prev.find((s) => s.field === field);
            if (additive) {
                if (existing) return prev.map((s) => (s.field === field ? { ...s, dir: flip(s.dir) } : s));
                return [...prev, { field, dir: 'asc' }];
            }
            if (existing && prev.length === 1) return [{ field, dir: flip(existing.dir) }];
            return [{ field, dir: 'asc' }];
        });
        setPageState(0);
    }, []);

    const setSingleSort = useCallback((field: string, dir: 'asc' | 'desc') => {
        setSort([{ field, dir }]);
        setPageState(0);
    }, []);

    const setFilter = useCallback((field: string, op: string, value: unknown) => {
        setSpotlightKeys(null);
        setFilters((prev) => {
            const next = { ...prev };
            if (value === '' || value == null) delete next[field];
            else next[field] = { field, op, value };
            return next;
        });
        setPageState(0);
    }, []);

    const clearFilters = useCallback(() => {
        setSpotlightKeys(null);
        // Идемпотентно: если фильтров нет — не меняем идентичность объекта, иначе
        // зря пересоздадим loadRoot и перезапросим данные (лоадер + рефетч).
        setFilters((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        setPageState(0);
    }, []);

    // Массовая видимость колонок (для сохранённых видов): show = не в списке скрытых.
    const setColumnsVisibility = useCallback((hidden: string[]) => {
        const hiddenSet = new Set(hidden);
        setColumns((prev) => prev.map((c) => (c.showInColumnChooser ? { ...c, show: !hiddenSet.has(c.field) } : c)));
    }, []);

    // Полная замена сортировки (для сохранённых видов).
    const setSortState = useCallback((next: IAttachSort[]) => {
        setSort(next);
        setPageState(0);
    }, []);

    const refresh = useCallback(() => {
        void loadRoot();
    }, [loadRoot]);

    const buildExportLoadOptions = useCallback(
        () => buildLoadOptions({ sort, filters: activeFilters() }),
        [sort, activeFilters],
    );

    // Клиентская сортировка всего набора и нарезка на текущую страницу.
    const allSortedRows = useMemo(() => sortRows(rows, sort, columns), [rows, sort, columns]);
    const sortedRows = useMemo(() => spotlightKeys ? allSortedRows.filter((row) => spotlightKeys.includes(row.key)) : allSortedRows,
        [allSortedRows, spotlightKeys]);
    const pagedRows = useMemo(() => {
        // Кламп на случай, если набор сжался (удаление + refresh) при page за его
        // пределами: иначе пустая страница, а пейджер при малом наборе скрыт —
        // выбраться некуда. Пейджер и так рисует текущую страницу с тем же клампом.
        const maxPage = Math.max(0, Math.ceil(sortedRows.length / Math.max(1, pageSize)) - 1);
        const start = Math.min(page, maxPage) * pageSize;
        return sortedRows.slice(start, start + pageSize);
    }, [sortedRows, page, pageSize]);

    return {
        ready,
        loadedChangeToken,
        loadedSpotlightKeys,
        spotlightKeys,
        setSpotlight,
        columns,
        schemeColumns,
        options,
        rows: pagedRows,
        allRows: allSortedRows,
        totalCount: spotlightKeys ? sortedRows.length : totalCount,
        loading,
        error,
        page,
        pageSize,
        search,
        sort,
        filters,
        expandedKeys,
        childrenByParent,
        loadingChildren,
        setPage,
        setPageSize,
        setSearch,
        toggleSort,
        setSingleSort,
        setFilter,
        clearFilters,
        setColumnsVisibility,
        setSort: setSortState,
        toggleExpand,
        refresh,
        buildExportLoadOptions,
    };
}
